import React, { useState } from 'react';
import { FormHelperText, Skeleton, Box, InputAdornment } from '@mui/material';
import { Search } from 'lucide-react';
import { Accordion, AlertBox, AlertBoxMessage, TextField, LoadingButton } from '../../../index';

export interface FilterSectionProps {
  /**
   * The filter key (category of filters, i.e. Priority)
   */
  filterKey: string;
  /**
   * Accordion title
   */
  filterSectionTitle: string;
  /**
   * The filter items for this filter section
   */
  children: React.ReactNode | React.ReactNode[];
  /**
   * The state value for search
   */
  searchValue?: string;
  /**
   * The label/placeholder text for the search input box
   */
  searchLabel?: string;
  /**
   * The onChange handler for the search input box
   */
  searchOnChange?: (e: React.SyntheticEvent, filterKey: string) => void;
  /**
   * Should it be open by default
   */
  isExpandedOnMount?: boolean;
  /**
   * The translation function
   */
  t: (key: string, options?: Record<string, string | number>) => string;
  /**
   * Whether the filter section is in a loading state
   */
  isLoading?: boolean;
  /**
   * Whether there was an error loading the filter section
   */
  isError?: boolean;
  /**
   * The count of selected items to display in the accordion header
   */
  itemsCount?: number;
  /**
   * The "See more" button props
   */
  seeMoreBtn?: {
    /**
     * Whether there are more results to load
     */
    hasMore: boolean;
    /**
     * Total number of results (displayed in "See more" button)
     */
    totalResults: number;
    /**
     * Callback when "See more" button is clicked
     */
    onSeeMore: () => void;
    /**
     * Whether "See more" is loading
     */
    isSeeMoreLoading: boolean;
  };
}

/**
 * Loading skeleton component that matches the checkbox filter item design
 */
const LoadingSkeleton: React.FC = () => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '8px 16px',
      borderRadius: '12px',
      backgroundColor: '#FFFFFF',
    }}
  >
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: '4px' }}>
      <Skeleton variant="text" width="70%" height={20} sx={{ borderRadius: '4px' }} />
      <Skeleton variant="text" width="40%" height={20} sx={{ borderRadius: '4px' }} />
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: '8px' }}>
      <Skeleton variant="rounded" width={24} height={24} sx={{ borderRadius: '6px' }} />
    </Box>
  </Box>
);

/**
 * Renders a FilterSection component which provides an expandable accordion, filter items can be passed as children
 */
const FilterSection: React.FC<React.PropsWithChildren<FilterSectionProps>> = ({
  filterKey,
  filterSectionTitle,
  children,
  searchOnChange,
  searchValue,
  searchLabel,
  isExpandedOnMount,
  t,
  isLoading,
  isError,
  itemsCount,
  seeMoreBtn,
}: FilterSectionProps) => {
  const [defaultExpanded] = useState(isExpandedOnMount);
  const noChildren = !children || (Array.isArray(children) && children.length === 0);

  return (
    <Accordion
      idPrefix={`filter-section--${filterKey}`}
      defaultExpanded={defaultExpanded}
      title={filterSectionTitle}
      chipCount={itemsCount}
      styleMode="compact"
      content={
        <>
          {isError ? (
            <AlertBox alertType="error" data-testid={`${filterKey}-section-error`}>
              <AlertBoxMessage>{t('ns.common:filter_section_error')}</AlertBoxMessage>
            </AlertBox>
          ) : (
            <>
              {searchValue !== undefined && (
                <Box sx={{ marginBottom: '16px' }}>
                  <TextField
                    id={`${filterKey}-search`}
                    name={`${filterKey}-search-input`}
                    value={searchValue}
                    onChange={(e) => searchOnChange?.(e, filterKey)}
                    placeholder={searchLabel}
                    data-testid={`${filterKey}-search-input`}
                    fullWidth
                    size="small"
                    sx={{
                      '& .MuiInputBase-input': {
                        paddingLeft: '0px',
                      },
                    }}
                    slotProps={{
                      input: {
                        startAdornment: (
                          <InputAdornment position="start">
                            <Search size={20} color="#A4A7AE" strokeWidth={1.7} />
                          </InputAdornment>
                        ),
                      },
                    }}
                  />
                </Box>
              )}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '6px',
                }}
              >
                {isLoading && (
                  <>
                    <LoadingSkeleton />
                    <LoadingSkeleton />
                    <LoadingSkeleton />
                  </>
                )}
                {!noChildren && !isLoading && children}
                {noChildren && !isLoading && (
                  <FormHelperText
                    data-testid="no-filter-search-results"
                    sx={{
                      color: '#535862',
                      fontSize: '14px',
                      lineHeight: '20px',
                      margin: 0,
                      padding: '8px 16px',
                    }}
                  >
                    {t('ns.task_filter:no_search_results')}
                  </FormHelperText>
                )}
                {seeMoreBtn?.hasMore && (
                  <LoadingButton
                    isLoading={seeMoreBtn.isSeeMoreLoading}
                    onClick={seeMoreBtn.onSeeMore}
                    variant="text"
                    dataTestId={`${filterKey}-filter-see-more`}
                  >
                    {t('ns.common:filter:search:see_more', { totalResults: seeMoreBtn.totalResults })}
                  </LoadingButton>
                )}
              </Box>
            </>
          )}
        </>
      }
    />
  );
};

export { FilterSection };
